/**
 * Thin fetch wrapper that attaches the Cognito ID token from useAuth to every
 * request and decodes the standard `{ error, detail? }` envelope on failure.
 *
 * Endpoints come from the admin Lambda's route table:
 *   GET    /sources                 → ListSources
 *   POST   /sources                 → CreateSource
 *   PUT    /sources/{id}            → UpdateSource
 *   DELETE /sources/{id}            → DeleteSource
 *   GET    /routing-rules           → ListRules
 *   POST   /routing-rules           → CreateRule
 *   PUT    /routing-rules/{id}      → UpdateRule
 *   DELETE /routing-rules/{id}      → DeleteRule
 */

import { config } from './config';

export interface ApiError {
  readonly status: number;
  readonly error: string;
  readonly detail?: string;
}

export class ApiCallError extends Error {
  readonly status: number;
  readonly error: string;
  readonly detail?: string;

  constructor(payload: ApiError) {
    super(`${payload.error}${payload.detail !== undefined ? `: ${payload.detail}` : ''}`);
    this.status = payload.status;
    this.error = payload.error;
    this.detail = payload.detail;
  }
}

interface RequestOpts {
  readonly idToken: string;
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOpts): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${opts.idToken}`,
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${config.apiEndpoint}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Server returned non-JSON body — surface raw text in the error.
      payload = { error: 'invalid_response', detail: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    // Three failure shapes worth distinguishing:
    //   1. Admin Lambda's stable envelope: `{ error, detail? }` — e.g. validation_failed.
    //   2. API Gateway JWT-authorizer rejection on missing/expired/bogus
    //      token: `{ message: "Unauthorized" }` with status 401. The Cognito
    //      ID token is 1h; a long browsing session will trip this every time.
    //      Surface as `session_expired` so the UI can sign-out + redirect.
    //   3. Anything else (CORS preflight failure, runtime crash,
    //      misconfigured Lambda, etc.) → invalid_response with the raw body.
    const errPayload = (typeof payload === 'object' && payload !== null
      ? payload
      : null) as { error?: string; detail?: string; message?: string } | null;

    let errCode: string;
    let errDetail: string | undefined;
    if (errPayload?.error !== undefined) {
      errCode = errPayload.error;
      errDetail = errPayload.detail;
    } else if (res.status === 401) {
      errCode = 'session_expired';
      errDetail = errPayload?.message ?? 'Sign in again to continue.';
      // Notify AuthProvider so it can clear sessionStorage + redirect.
      // Custom event keeps api.ts decoupled from auth.tsx.
      window.dispatchEvent(new CustomEvent('feed-admin:session-expired'));
    } else if (errPayload?.message !== undefined) {
      errCode = `http_${res.status}`;
      errDetail = errPayload.message;
    } else {
      errCode = 'unknown_error';
      errDetail = `HTTP ${res.status}`;
    }

    throw new ApiCallError({
      status: res.status,
      error: errCode,
      detail: errDetail,
    });
  }

  return payload as T;
}

// =============================================================================
// Type aliases — mirror the admin λ response envelopes.
// =============================================================================

export interface FeedSource {
  readonly id: string;
  readonly name: string;
  readonly category: 'person' | 'publication' | 'channel' | 'feed';
  readonly priority: 'critical' | 'high' | 'normal' | 'low';
  readonly source_type: string;
  readonly licence_class: 'public_rss' | 'paywalled_rss' | 'personal_only' | 'do_not_quote';
  readonly active: boolean;
  readonly poll_interval_minutes: number;
  readonly next_poll_at: string;
  readonly rss_url: string | null;
  readonly youtube_channel_id: string | null;
  readonly youtube_handle: string | null;
  readonly twitter_handle: string | null;
  readonly bluesky_handle: string | null;
  readonly mastodon_url: string | null;
  readonly blog_url: string | null;
  readonly newsletter_url: string | null;
  readonly website_url: string | null;
  readonly sitemap_url_filter: string | null;
  readonly tags: readonly string[];
  readonly expertise: string | null;
  readonly failure_count: number;
  readonly last_failure_at: string | null;
  readonly rolling_item_count: number | null;
  readonly rolling_avg_quality: number | null;
  readonly rolling_a_rate: number | null;
  readonly rolling_updated_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface FeedItem {
  readonly id: string;
  readonly title: string;
  readonly url: string | null;
  readonly author: string | null;
  readonly published_at: string | null;
  readonly ingested_at: string;
  readonly source_id: string;
  readonly source_name: string;
  readonly source_type: string;
  readonly source_tags: readonly string[];
  readonly tier: 'S' | 'A' | 'B' | 'C' | null;
  readonly quality_score: number | null;
  readonly labels: readonly string[];
  readonly summary_short: string | null;
  readonly summary_medium: string | null;
  readonly routed_destinations: readonly string[];
}

export interface ListItemsFilters {
  readonly tier?: readonly ('S' | 'A' | 'B' | 'C')[];
  readonly source_type?: string;
  readonly tag?: string;
  readonly from?: string;
  readonly limit?: number;
  readonly offset?: number;
}

export interface RoutingRule {
  readonly id: string;
  readonly name: string;
  readonly match_expression: Record<string, unknown>;
  readonly destinations: readonly string[];
  readonly licence_class_override: string | null;
  readonly active: boolean;
  readonly priority: number | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface DigestSummary {
  readonly week_end: string;
  readonly size_bytes: number;
  readonly last_modified: string;
  readonly s3_key: string;
}

export interface DigestDetail {
  readonly week_end: string;
  readonly html: string;
  readonly size_bytes: number;
}

export interface AuroraStatus {
  readonly cluster_status: string;
  readonly capacity_acu: number | null;
  readonly state: 'warm' | 'paused' | 'starting' | 'stopped' | 'unknown';
  readonly fetched_at: string;
}

// =============================================================================
// API surface
// =============================================================================

export const api = {
  listDigests: (idToken: string, signal?: AbortSignal) =>
    request<{ digests: readonly DigestSummary[]; count: number }>('/digests', { idToken, signal }),

  getDigest: (idToken: string, week: string, signal?: AbortSignal) =>
    request<DigestDetail>(`/digests/${week}`, { idToken, signal }),

  getAuroraStatus: (idToken: string, signal?: AbortSignal) =>
    request<AuroraStatus>('/aurora/status', { idToken, signal }),

  listSources: (idToken: string, signal?: AbortSignal) =>
    request<{ sources: readonly FeedSource[]; count: number }>('/sources', { idToken, signal }),

  createSource: (idToken: string, body: Partial<FeedSource>) =>
    request<{ source: FeedSource }>('/sources', { idToken, method: 'POST', body }),

  updateSource: (idToken: string, id: string, body: Partial<FeedSource>) =>
    request<{ source: FeedSource }>(`/sources/${id}`, { idToken, method: 'PUT', body }),

  deleteSource: (idToken: string, id: string) =>
    request<void>(`/sources/${id}`, { idToken, method: 'DELETE' }),

  listRules: (idToken: string, signal?: AbortSignal) =>
    request<{ rules: readonly RoutingRule[]; count: number }>('/routing-rules', { idToken, signal }),

  createRule: (idToken: string, body: Partial<RoutingRule>) =>
    request<{ rule: RoutingRule }>('/routing-rules', { idToken, method: 'POST', body }),

  updateRule: (idToken: string, id: string, body: Partial<RoutingRule>) =>
    request<{ rule: RoutingRule }>(`/routing-rules/${id}`, { idToken, method: 'PUT', body }),

  deleteRule: (idToken: string, id: string) =>
    request<void>(`/routing-rules/${id}`, { idToken, method: 'DELETE' }),

  // -----------------------------------------------------------------------
  // P6.6 — manual digest trigger
  // -----------------------------------------------------------------------
  // dryRun=true (default): re-renders the digest, writes Markdown to S3,
  //   updates digests/digest_items provenance — but does NOT send via SES.
  //   Useful for previewing the rendered content of a recent week.
  // dryRun=false: sends via SES on top of the dry-run path.
  triggerDigest: (
    idToken: string,
    opts: { dryRun: boolean; week_start?: string; week_end?: string },
  ) =>
    request<{
      triggered: boolean;
      dry_run: boolean;
      payload: { week_start?: string; week_end?: string; ses_send?: boolean };
      result: {
        digest_id: string | null;
        week_start: string;
        week_end: string;
        item_count: number;
        s3_markdown_key: string;
        s3_html_key: string;
        ses_message_id: string | null;
        truncated: boolean;
        warnings: readonly string[];
      };
    }>('/digest/trigger', {
      idToken,
      method: 'POST',
      body: {
        ses_send: !opts.dryRun,
        week_start: opts.week_start,
        week_end: opts.week_end,
      },
    }),

  // -----------------------------------------------------------------------
  // Items feed — read-only "what's been ingested" view
  // -----------------------------------------------------------------------
  // All filters are optional. Server clamps limit to [1,200] and applies
  // sensible defaults (tier IS NOT NULL — only rated items, sorted by
  // ingested_at DESC, limit=50).
  listItems: (idToken: string, filters: ListItemsFilters, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (filters.tier !== undefined && filters.tier.length > 0) {
      params.set('tier', filters.tier.join(','));
    }
    if (filters.source_type !== undefined && filters.source_type !== '') {
      params.set('source_type', filters.source_type);
    }
    if (filters.tag !== undefined && filters.tag !== '') {
      params.set('tag', filters.tag);
    }
    if (filters.from !== undefined && filters.from !== '') {
      params.set('from', filters.from);
    }
    if (filters.limit !== undefined) {
      params.set('limit', String(filters.limit));
    }
    if (filters.offset !== undefined) {
      params.set('offset', String(filters.offset));
    }
    const qs = params.toString();
    const path = qs === '' ? '/items' : `/items?${qs}`;
    return request<{
      items: readonly FeedItem[];
      count: number;
      limit: number;
      offset: number;
    }>(path, { idToken, signal });
  },
};
